import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Clock, Flame, TrendingUp, Zap, Anchor, Sparkles } from "lucide-react";

const ICON = { LEAN: Flame, BUILD: TrendingUp, STRONG: Zap, MAINTAIN: Anchor };
const ACCENT = { LEAN: "#E65322", BUILD: "#3B82F6", STRONG: "#10B981", MAINTAIN: "#7C3AED" };

// Week-12 teaser — shown while the Reset is still running.
export function PathwayCountdown({ days }) {
  if (days > 7) return null;
  return (
    <div className="bg-card border border-primary/40 rounded-lg p-5 flex items-center gap-4" data-testid="pathway-countdown">
      <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Clock size={18} /></div>
      <div>
        <div className="label-eyebrow">Almost there</div>
        <div className="font-head uppercase text-lg leading-snug mt-1">
          {days === 0 ? "Your Reset finishes today." : `${days} day${days === 1 ? "" : "s"} until you choose your next pathway.`}
        </div>
        <div className="text-xs text-muted-foreground mt-1">LEAN · BUILD · STRONG · MAINTAIN — no new checkout, your membership simply continues.</div>
      </div>
    </div>
  );
}

// Post-Reset chooser. Handles unavailable pathways (STRONG / MAINTAIN) with a waitlist state.
export function ChoosePathway({ chosen, refresh }) {
  const [pathways, setPathways] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get("/pathways").then((r) => setPathways(r.data.pathways)).catch(() => {}); }, []);

  const choose = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/user/pathway", { pathway: id });
      if (data.available && data.route) { toast.success(`BA30 ${id} unlocked.`); navigate(data.route); }
      else { toast.success(`You're first in line for BA30 ${id}.`); }
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const chosenMeta = pathways.find((p) => p.id === chosen);
  if (chosenMeta && !chosenMeta.available) {
    return (
      <div className="rounded-lg border-2 p-8" style={{ borderColor: ACCENT[chosen] }} data-testid="pathway-waitlist">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-eyebrow" style={{ color: ACCENT[chosen] }}>BA30 {chosen} · Coming soon</div>
            <h2 className="wordmark mt-3 text-3xl md:text-4xl uppercase leading-[0.95]">You're first in line.</h2>
            <p className="mt-4 max-w-xl text-sm text-foreground/75 leading-relaxed">
              BA30 {chosen} is being built. We'll email you the moment it opens — it's included in your membership, no new checkout.
              Until then, keep the system running: 10K steps, three sessions a week, mostly whole foods.
            </p>
          </div>
          <Sparkles size={28} style={{ color: ACCENT[chosen] }} />
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          {pathways.filter((p) => p.available).map((p) => (
            <button key={p.id} onClick={() => choose(p.id)} disabled={busy} data-testid={`switch-to-${p.id.toLowerCase()}`}
              className="inline-flex items-center gap-2 text-xs font-head uppercase tracking-widest border border-border px-4 py-2 rounded-md hover:bg-foreground hover:text-background transition-colors disabled:opacity-60">
              Start {p.id} now instead <ArrowRight size={14} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary text-primary-foreground rounded-lg p-8" data-testid="choose-pathway">
      <div className="label-eyebrow !text-primary-foreground/80">Foundation complete</div>
      <h2 className="wordmark mt-3 text-3xl md:text-5xl uppercase leading-[0.95]">Now choose what you want to <span className="underline">build.</span></h2>
      <p className="mt-3 text-sm text-primary-foreground/85 max-w-xl">Your membership continues as-is. Pick the pathway that matches your next goal — you can change later.</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {pathways.map((p) => {
          const Icon = ICON[p.id];
          return (
            <button key={p.id} onClick={() => choose(p.id)} disabled={busy}
              className="text-left bg-primary-foreground text-foreground rounded-md p-5 lift disabled:opacity-60 relative"
              data-testid={`pathway-choose-${p.id.toLowerCase()}`}>
              {!p.available && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-foreground text-background px-2 py-0.5 rounded">Coming soon</span>}
              <Icon size={18} style={{ color: ACCENT[p.id] }} />
              <div className="font-display uppercase text-xl mt-3">{p.id}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{p.label}</div>
              <div className="mt-2 text-xs text-foreground/70 leading-snug">{p.desc}</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest" style={{ color: ACCENT[p.id] }}>{p.available ? "Start now →" : "Join the list →"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
